import { useToast, Stack, Box } from '@chakra-ui/react';
import { ActionDisplayInfo, ChatMessage, resolveContentText } from '@worknet/models';
import { ComponentProps, useEffect, useRef, useState } from 'react';

import useChatStore from '~/shared/utils/useChatStore';
import ScrollPoint from '~/shared/components/HostedApp/Messages/ScrollPoint.tsx';
import { openCitationSource } from '~/shared/utils/linkTarget';

import ServerMessage from '../../ds/ServerMessage';
import UserInput from '../../ds/UserInput';
import UserMessage from '../../ds/UserMessage';
import TextboxModal from '../../ds/TextboxModal';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import ServerLoadingMessage from '../../ds/ServerLoadingMessage';
import { fadeIn } from '../utils';
import { FileDropZone } from '../../ds/FileDropZone';

import MessageContainer from './MessageContainer';
import DefaultMessage from './DefaultMessage';
import CheckboxWithText from './CheckboxWithText';
import AiMessageBody from './AiMessageBody';

type SubmitType = Parameters<NonNullable<ComponentProps<typeof ServerMessage>['onSubmit']>>[0];
type ModelConfig = Omit<ComponentProps<typeof TextboxModal<ActionDisplayInfo>>, 'onClose'>;

function getElementWithoutCitations(element: HTMLElement) {
  const clonedElement = element.cloneNode(true) as HTMLElement;
  clonedElement.querySelectorAll('[data-is-citation="true"]').forEach((el) => el.remove());
  return clonedElement;
}

type AutoScrollState = 'auto' | 'paused' | 'disabled';

export default function Messages() {
  const {
    appValue,
    chatValue,
    sendMessage,
    setMessageLiked,
    setChatSharingConfirmed,
    hostPlatformContext,
    uploadWarning,
    pendingText,
  } = useChatStore(
    ({
      app,
      chat,
      hostPlatformContext,
      uploadWarning,
      pendingText,
      //
      sendMessage,
      setMessageLiked,
      setChatSharingConfirmed,
    }) => ({
      hostPlatformContext,
      appValue: app.value!,
      chatValue: chat!.value!,
      sendMessage,
      setMessageLiked,
      setChatSharingConfirmed,
      uploadWarning,
      pendingText,
    })
  );

  const { user, featureOverrides, groupingId } = hostPlatformContext!;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [modalConfig, setCurrentModalConfig] = useState<ModelConfig>();
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const isNewAiLayer = appValue.amaAction.useNewAiLayer;
  const chatOwner = chatValue.chat?.user;
  const isChatOwner = !chatValue.messages.at(0) || chatOwner?.email === user?.email;

  const [autoScrollState, setAutoScrollState] = useState<AutoScrollState>('disabled');
  const isPendingAction = Boolean(chatValue.sending || chatValue.progressingBotMessage);
  const isSubmitFormLoading = isPendingAction || !!chatValue.progressingBotMessage;

  const toast = useToast();

  useEffect(() => {
    if (uploadWarning) {
      toast({
        title: 'Upload Error',
        description: uploadWarning,
        status: 'warning',
        duration: 8000,
        isClosable: true,
      });
      useChatStore.setState({ uploadWarning: undefined });
    }
  }, [uploadWarning, toast]);

  const handleFilesDropped = (files: File[]) => {
    setDroppedFiles(files);
  };

  useEffect(() => {
    if (isPendingAction && autoScrollState !== 'paused') {
      setAutoScrollState('auto');
    }
  }, [autoScrollState, isPendingAction]);

  useEffect(() => {
    if (autoScrollState !== 'auto') {
      return;
    }
    if (!chatValue.progressingBotMessage) {
      return;
    }
    let frame: number;
    const loop = () => {
      const sc = scrollContainerRef.current;
      if (sc) {
        const distance = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
        if (distance < 600) {
          sc.scrollTop = sc.scrollHeight - sc.clientHeight;
        }
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [autoScrollState, Boolean(chatValue.progressingBotMessage)]);

  const isSharedCheckboxShown =
    !!appValue.sharingApprovalRequired &&
    !!groupingId &&
    !!appValue.groupingSupported &&
    !chatValue.chat;

  function onTextboxModalClose(action: ActionDisplayInfo, text?: string) {
    setCurrentModalConfig(undefined);
    if (!text) {
      return;
    }
    sendMessage({ actionId: action.id, content: text });
  }

  function onSendFeedbackSuccess(isLiked: boolean) {
    toast({
      title: isLiked ? 'Liked' : 'Disliked',
      status: 'success',
      description: 'Thank you for your feedback',
      isClosable: true,
      duration: 3000,
    });
  }

  function onActionClick(action: ActionDisplayInfo) {
    if (action.userInputRequired) {
      setCurrentModalConfig({
        data: action,
        button: 'Submit',
        placeholder: 'Enter text',
        title: action.displayName,
        required: true,
      });
      return;
    }

    sendMessage({ actionId: action.id, content: action.displayName });
  }

  async function onServerMessageCommand(
    type: SubmitType,
    value: string,
    msg: ChatMessage,
    args: Record<string, string>
  ) {
    if (msg.role !== 'ai') {
      return;
    }

    if ('error' in msg.content) {
      console.error(msg.content.error);
      return;
    }

    try {
      setInteractionLoading(true);
      if (type === 'button') {
        switch (value) {
          case 'dislike':
          case 'like':
            {
              setMessageLiked(msg.id, value === 'like', args?.text, onSendFeedbackSuccess);
            }
            break;
          case 'copy':
            {
              const el = document.querySelector<HTMLElement>(
                `[data-message-id="${msg.id}"] .worknet-markdown`
              );
              const elWithoutCitations = getElementWithoutCitations(el!);
              if (featureOverrides?.copyToClipboard) {
                await featureOverrides.copyToClipboard?.(elWithoutCitations, msg.content.message);
              } else {
                await copyToClipboard(elWithoutCitations, msg.content.message);
              }
              toast({
                title: 'Copied!',
                status: 'success',
                description: 'Thanks message was copied',
                isClosable: false,
                duration: 500,
              });
            }
            break;
        }
      } else {
        openCitationSource(value);
      }
    } catch (error) {
      toast({
        title: 'Error',
        status: 'error',
        description: (error as Error).message,
        isClosable: true,
      });
    } finally {
      setInteractionLoading(false);
    }
  }

  const isDefaultMessageDisplayed =
    chatValue.messages.length === 0 &&
    !(appValue?.autoAction && chatValue?.sending) &&
    !(appValue?.amaAction?.useNewAiLayer && chatValue?.sending) &&
    !chatValue.progressingBotMessage;

  const isCopyButtonHidden = featureOverrides?.copyToClipboard === false;

  return (
    <>
      <FileDropZone onFilesDropped={handleFilesDropped} disableDropZone={!isNewAiLayer}>
        <Stack h="100%">
          <Stack
            flex={1}
            ref={scrollContainerRef}
            overflowY="auto"
            overflowX="hidden"
            onWheel={(e) => {
              if (autoScrollState === 'auto' && isPendingAction && e.deltaY < 0) {
                const sc = scrollContainerRef.current;
                if (!sc) {
                  return;
                }
                const distanceFromBottom = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
                if (distanceFromBottom > 0) {
                  setAutoScrollState('paused');
                }
              }
            }}
          >
            {isDefaultMessageDisplayed ? (
              <Box
                flex={1}
                animation={`${fadeIn} 0.6s`}
                pointerEvents={isPendingAction ? 'none' : 'auto'}
              >
                <DefaultMessage
                  chatSettings={appValue}
                  hostPlatformContext={hostPlatformContext!}
                  actions={appValue.actions}
                  onActionClick={onActionClick}
                />
              </Box>
            ) : (
              <>
                <Stack
                  gap="20px"
                  pb="20px"
                  flex={1}
                  minW={0}
                  ref={scrollContentRef}
                  pointerEvents={isPendingAction ? 'none' : 'auto'}
                >
                  {chatValue.messages.map((msg, i) => {
                    const isBot = msg.role === 'ai' || msg.role === 'system';
                    const isLastMessage = i === chatValue.messages.length - 1;
                    const otherUsersArr = Object.values(chatValue.chat?.otherUsers || {});

                    if (msg.role === 'system' && msg.hidden) {
                      return null;
                    }

                    const msgUser =
                      msg.role === 'human'
                        ? otherUsersArr.find(({ email }) => email === msg.userEmail)
                        : chatOwner;

                    return (
                      <MessageContainer
                        key={i}
                        createdAt={new Date(msg.audit.createdAt)}
                        isBot={isBot}
                        chatSettings={appValue}
                        name={msgUser?.name}
                        imageSrc={msgUser?.picture}
                      >
                        {msg.role === 'ai' ? (
                          <AiMessageBody
                            msg={msg}
                            chatId={chatValue.chat?.id}
                            disclaimer={appValue.disclaimer}
                            isLastMessage={isLastMessage}
                            isChatOwner={isChatOwner}
                            isCopyButtonHidden={!!isCopyButtonHidden}
                            interactionLoading={interactionLoading}
                            onSubmit={(type, value, args) =>
                              onServerMessageCommand(type, value, msg, args)
                            }
                          />
                        ) : msg.role === 'system' ? (
                          <ServerMessage
                            messageId={msg.id}
                            message={resolveContentText(msg.content)}
                            hiddenButtons="all"
                          />
                        ) : (
                          <UserMessage
                            key={i}
                            isSameUser={msg.role === 'user'}
                            message={resolveContentText(msg.display ?? msg.content)}
                            attachmentIds={msg.attachments}
                          />
                        )}
                      </MessageContainer>
                    );
                  })}
                  {chatValue.progressingBotMessage && (
                    <MessageContainer createdAt={new Date()} isBot chatSettings={appValue}>
                      <ServerLoadingMessage
                        message={chatValue.progressingBotMessage.content}
                        featureOverrides={featureOverrides}
                      />
                    </MessageContainer>
                  )}
                </Stack>
                <ScrollPoint
                  scrollContainerRef={scrollContainerRef}
                  scrollContentRef={scrollContentRef}
                  active={autoScrollState === 'auto'}
                  onBottomScrolled={() => {
                    if (isPendingAction) {
                      setAutoScrollState('auto');
                    }
                  }}
                />
              </>
            )}
          </Stack>
          {isChatOwner && (
            <Stack mt="auto" gap="11px">
              {appValue.amaAction.userInputRequired && (
                <UserInput
                  isLoading={isSubmitFormLoading}
                  files={droppedFiles}
                  onClearFiles={() => setDroppedFiles([])}
                  allowFileUpload={isNewAiLayer}
                  conversationAttachmentSize={chatValue.chat?.totalAttachmentSize}
                  conversationAttachmentTokens={chatValue.chat?.totalAttachmentTokens}
                  pendingText={pendingText}
                  onSubmit={(text, files) => {
                    if (files && files.length > 0) {
                      sendMessage({
                        actionId: appValue.amaAction.id,
                        content: text,
                        attachments: files,
                      });
                    } else {
                      sendMessage({
                        actionId: appValue.amaAction.id,
                        content: text,
                      });
                    }
                    setAutoScrollState('auto');
                    setDroppedFiles([]);
                  }}
                />
              )}

              {isSharedCheckboxShown && (
                <CheckboxWithText
                  isChecked={!!chatValue.sharingConfirmed}
                  onChange={() => setChatSharingConfirmed(!chatValue.sharingConfirmed)}
                  text="This conversation will be shared with your group."
                />
              )}
            </Stack>
          )}
        </Stack>

        {!!modalConfig && <TextboxModal onClose={onTextboxModalClose} {...modalConfig} />}
        {/* {!!confirmationModalConfig && <ConfirmationModal {...confirmationModalConfig} />} */}
      </FileDropZone>
    </>
  );
}
